import {
  BuiltinEvent,
  FromServer,
} from "@websim/core/src/project_revision_types";
import { areObjectsEqual } from "../hyperbase/utils/areObjectsEqual";
import { deepMerge } from "../hyperbase/utils/deepMerge";
import { stableStringify } from "../hyperbase/utils/stableStringify";
import { parentApi } from "./message-proxy";
/**
 * Interface defining the public API for WebsimSocket room operations.
 */
export interface IWebsimSocketRoom {
  /**
   * Object containing information about the connected client and their peers.
   */
  party: {
    /**
     * Object containing the client ID, avatar URL, and username of this connected client.
     */
    client: {
      id: string;
      avatarUrl: string;
      username: string;
    };
    /**
     * Object containing all connected peers, including this client.
     * This is always up-to-date.
     */
    peers: {
      [clientId: string]: { avatarUrl: string; username: string };
    };
    subscribe: (
      callback: (peers: {
        [clientId: string]: { avatarUrl: string; username: string };
      }) => void
    ) => () => void;
    /**
     * Object containing the current presence state of all connected peers, including this client.
     * This is always up-to-date after initialization.
     */
    presence: {
      [clientId: string]: Record<string, any>;
    };

    /**
     * Updates the current client's presence state.
     * @param presence The new presence state to set.
     */
    updatePresence: (presence: Record<string, any>) => void;
    /**
     * Subscribe to presence updates from all peers.
     * @param callback Function to call when presence changes.
     * @returns Function to unsubscribe.
     */
    subscribePresence: (
      callback: (presence: { [clientId: string]: Record<string, any> }) => void
    ) => () => void;
    /**
     * Object containing the current room-wide state.
     * This is always up-to-date.
     */
    roomState: Record<string, any>;
  };

  /**
   * Legacy event handler for changes in connected peers.
   * @param peers An object with client IDs as keys, each containing the client's avatar URL and username.
   */
  onPeersChanged:
    | ((peers: {
        [clientId: string]: { avatarUrl: string; username: string };
      }) => any)
    | null;

  /**
   * Initialize the WebSocket connection.
   * @returns A promise that resolves when initialization is complete.
   */
  initialize(): Promise<void>;

  /**
   * Request a presence update from a specific client.
   * @param clientId The ID of the client to request an update from.
   * @param update The update to request.
   */
  requestPresenceUpdate(clientId: string, update: Record<string, any>): void;

  /**
   * Subscribe to presence update requests from other clients.
   * @param callback Function to call when a presence update is requested.
   * @returns Function to unsubscribe.
   */
  subscribePresenceUpdateRequests(
    callback: (updateRequest: Record<string, any>, fromClientId: string) => void
  ): () => void;

  /**
   * Updates the room-wide state. This merges with existing state.
   * @param delta The new state to merge with current room state.
   */
  updateRoomState(delta: Record<string, any>): void;

  /**
   * Subscribe to room state updates.
   * @param callback Function to call when room state changes.
   * @returns Function to unsubscribe.
   */
  subscribeRoomState(
    callback: (state: Record<string, any>) => void
  ): () => void;
}

const eventTypes = ["close", "error", "message", "open"] as const;

function isWebsocketEvent(type: string): type is (typeof eventTypes)[number] {
  return eventTypes.includes(type as any);
}

type SubscriptionCallback = (records: any[]) => void;
const cachedEmptyList: any[] = [];

interface CollectionAPI {
  getList: () => any[];
  create: (data: { [key: string]: any }) => Promise<{ [id: string]: any }>;
  update: (
    id: string,
    data: { [key: string]: any }
  ) => Promise<{ [id: string]: any }>;
  delete: (id: string) => Promise<void>;
  subscribe: (callback: (records: any[]) => void) => () => void;
  filter: (filters: { [key: string]: any }) => CollectionAPI;
}

export class WebsimSocket
  extends EventTarget
  implements WebSocket, IWebsimSocketRoom
{
  CONNECTING = 0 as const;
  OPEN = 1 as const;
  CLOSING = 2 as const;
  CLOSED = 3 as const;
  binaryType: BinaryType = "arraybuffer";
  bufferedAmount = 0;
  extensions = "";
  protocol: "ws" | "wss" = "ws";
  readyState: number = this.CONNECTING;
  url = "";

  private _client = {
    id: "",
    avatarUrl: "",
    username: "",
  };
  private static _requestIdPrefix = Math.random().toString(36).substring(2, 6); // 4 random characters
  private static _requestIdCounter = 0;

  private static _pendingRequests: Map<string, (data: any) => void> = new Map();

  private static _connectedPeers: {
    [clientId: string]: { avatarUrl: string; username: string };
  } = {};
  private static _peersSubscriptions: Set<
    (peers: {
      [clientId: string]: { avatarUrl: string; username: string };
    }) => void
  > = new Set();

  private static _presenceData: {
    [clientId: string]: Record<string, any>;
  } = {};
  private static _presenceDataRef: {
    [clientId: string]: Record<string, any>;
  } | null = null;
  private static _presenceSubscriptions: Set<
    (presence: { [clientId: string]: Record<string, any> }) => void
  > = new Set();

  private static _collectionCaches: Map<string, any[]> = new Map();
  private static _pendingCacheFetches: Set<string> = new Set();
  private static _filteredCollections: Map<string, CollectionAPI> = new Map();
  private static _filteredSubscriptions: Map<
    string,
    Set<SubscriptionCallback>
  > = new Map();

  private static _roomState: Record<string, any> = {};
  private static _roomStateRef: Record<string, any> | null = null;
  private static _roomStateSubscriptions: Set<
    (state: Record<string, any>) => void
  > = new Set();

  private static _instance: WebsimSocket | null = null;

  // Add throttling variables
  private _presenceThrottleTimeout: number | null = null;
  private _pendingPresenceUpdates: Record<string, any> | null = null;
  private _presenceThrottleMs = 16; //  (approximately 60fps)

  // Add throttling variables for room state
  private _roomStateThrottleTimeout: number | null = null;
  private _pendingRoomStateUpdates: Record<string, any> | null = null;
  private _roomStateThrottleMs = 16;
  private _initializePromise: Promise<void> | null = null;

  private static _presenceUpdateRequestSubscriptions: Set<
    (updateRequest: Record<string, any>, fromClientId: string) => void
  > = new Set();

  constructor() {
    if (WebsimSocket._instance) {
      // Singleton, to be more forgiving of repeat calls in user code
      return WebsimSocket._instance;
    }

    super();

    // Bind methods
    this.createDatabaseRecord = this.createDatabaseRecord.bind(this);
    this.updateDatabaseRecord = this.updateDatabaseRecord.bind(this);
    this.getDatabaseRecords = this.getDatabaseRecords.bind(this);
    this.deleteDatabaseRecord = this.deleteDatabaseRecord.bind(this);

    WebsimSocket._instance = this;

    this._initializePromise = this._initialize();
  }

  accept(): void {}
  serializeAttachment(): void {}
  deserializeAttachment(): void {}

  onclose: ((this: WebSocket, ev: CloseEvent) => any) | null = null;
  onerror: ((this: WebSocket, ev: Event) => any) | null = null;
  onmessage: ((this: WebSocket, ev: MessageEvent) => any) | null = null;
  onopen: ((this: WebSocket, ev: Event) => any) | null = null;
  dispatchEvent(
    event: Event | MessageEvent | CloseEvent | ErrorEvent
  ): boolean {
    if (isWebsocketEvent(event.type) && this[`on${event.type}`]) {
      this[`on${event.type}`]!(event as any);
    }

    return super.dispatchEvent(event);
  }
  onPeersChanged:
    | ((peers: {
        [clientId: string]: { avatarUrl: string; username: string };
      }) => any)
    | null = null;

  close(_code?: number, _reason?: string): void {
    // Clear all subscriptions
    console.log("closing websocket to ", this.url);
    WebsimSocket._filteredSubscriptions.clear();
    WebsimSocket._presenceSubscriptions.clear();
  }
  send(data: string | object): void {
    const message = typeof data === "string" ? data : JSON.stringify(data);
    parentApi.sendSocketMessage(message);
  }

  get party() {
    return {
      client: this._client,
      peers: WebsimSocket._connectedPeers,
      subscribe: (
        callback: (peers: {
          [clientId: string]: { avatarUrl: string; username: string };
        }) => void
      ) => {
        WebsimSocket._peersSubscriptions.add(callback);
        // Immediately call with current peers data
        callback(WebsimSocket._connectedPeers);
        return () => {
          WebsimSocket._peersSubscriptions.delete(callback);
        };
      },
      // Return the current presence reference, which only changes when data changes
      get presence() {
        if (!WebsimSocket._presenceDataRef) {
          WebsimSocket._presenceDataRef = { ...WebsimSocket._presenceData };
        }
        return WebsimSocket._presenceDataRef;
      },
      updatePresence: (presence: Record<string, any>) => {
        const clonedPresence = structuredClone(presence);
        this._updatePresence(clonedPresence);
      },
      subscribePresence: (
        callback: (presence: {
          [clientId: string]: Record<string, any>;
        }) => void
      ) => {
        WebsimSocket._presenceSubscriptions.add(callback);
        // Immediately call with current presence data
        if (!WebsimSocket._presenceDataRef) {
          WebsimSocket._presenceDataRef = { ...WebsimSocket._presenceData };
        }
        callback(WebsimSocket._presenceDataRef);
        return () => {
          WebsimSocket._presenceSubscriptions.delete(callback);
        };
      },
      get roomState() {
        if (!WebsimSocket._roomStateRef) {
          WebsimSocket._roomStateRef = { ...WebsimSocket._roomState };
        }
        return WebsimSocket._roomStateRef;
      },
    };
  }

  private async _initialize() {
    try {
      const result = await parentApi.connectSocket();
      if (result === null) {
        throw new Error("Failed to connect to socket");
      }

      const { stream, username, avatarUrl, connectionId } = result;

      this.readyState = this.OPEN;

      this._client = {
        id: connectionId,
        avatarUrl,
        username,
      };

      this.dispatchEvent(
        new Event("open", { bubbles: false, cancelable: false })
      );

      // Initialize empty presence for this client
      WebsimSocket._presenceData[connectionId] = {};

      this.handleMessageStream(stream);

      // Get initial presence data and room state for all connected clients
      await Promise.all([this.getPresence(), this.getRoomState()]);
    } catch (error: any) {
      console.error("Error connecting to socket:", error);
    }
  }

  private async handleMessageStream(stream: ReadableStream<Uint8Array>) {
    const reader = stream.getReader();
    const decoder = new TextDecoder();

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      const message = JSON.parse(decoder.decode(value));

      if (message.requestId !== undefined) {
        const resolve = WebsimSocket._pendingRequests.get(message.requestId);
        if (resolve) {
          resolve(message.data);
          WebsimSocket._pendingRequests.delete(message.requestId);
        }
      }

      switch (message.type) {
        case BuiltinEvent.GET_CONNECTED_CLIENTS:
          WebsimSocket._connectedPeers = message.clients;
          this.onPeersChanged?.(WebsimSocket._connectedPeers);
          WebsimSocket._peersSubscriptions.forEach((callback) =>
            callback(WebsimSocket._connectedPeers)
          );
          break;
        case FromServer.Connected:
          WebsimSocket._connectedPeers[message.clientId] = {
            avatarUrl: message.avatarUrl,
            username: message.username,
          };
          // Initialize empty presence for new client
          WebsimSocket._presenceData[message.clientId] = {};
          this.onPeersChanged?.(WebsimSocket._connectedPeers);
          WebsimSocket._peersSubscriptions.forEach((callback) =>
            callback(WebsimSocket._connectedPeers)
          );
          break;
        case FromServer.Disconnected:
          delete WebsimSocket._connectedPeers[message.clientId];
          // Remove presence data for disconnected client
          delete WebsimSocket._presenceData[message.clientId];
          this.onPeersChanged?.(WebsimSocket._connectedPeers);
          WebsimSocket._peersSubscriptions.forEach((callback) =>
            callback(WebsimSocket._connectedPeers)
          );
          WebsimSocket._presenceSubscriptions.forEach((callback) =>
            callback(WebsimSocket._presenceData)
          );
          break;
        case FromServer.PresenceUpdated: {
          // Update presence data for a specific client
          const oldClientPresence =
            WebsimSocket._presenceData[message.clientId];
          const newClientPresence = message.presence;

          // Check if there's an actual change
          if (!areObjectsEqual(oldClientPresence, newClientPresence)) {
            // Update the data
            WebsimSocket._presenceData[message.clientId] = newClientPresence;

            // Create a new reference directly
            WebsimSocket._presenceDataRef = { ...WebsimSocket._presenceData };

            // Notify subscribers with the new reference
            WebsimSocket._presenceSubscriptions.forEach((callback) => {
              if (WebsimSocket._presenceDataRef) {
                callback(WebsimSocket._presenceDataRef);
              }
            });
          }
          break;
        }
        case BuiltinEvent.GET_PRESENCE: {
          // Check if there's an actual change
          if (!areObjectsEqual(WebsimSocket._presenceData, message.presence)) {
            // Update the data
            WebsimSocket._presenceData = message.presence;

            // Create a new reference directly
            WebsimSocket._presenceDataRef = { ...WebsimSocket._presenceData };

            // Notify subscribers with the new reference
            WebsimSocket._presenceSubscriptions.forEach((callback) => {
              if (WebsimSocket._presenceDataRef) {
                callback(WebsimSocket._presenceDataRef);
              }
            });
          }
          break;
        }
        case FromServer.Reset:
          window.location.reload();
          break;
        case BuiltinEvent.GET_DB_RECORDS:
          break;
        case BuiltinEvent.CREATE_DB_RECORD:
          this.handleSubscription({
            _action: "create",
            record: message.data,
          });
          break;
        case BuiltinEvent.DELETE_DB_RECORD:
          this.handleSubscription({
            _action: "delete",
            record: message.data,
          });
          break;
        case BuiltinEvent.UPDATE_DB_RECORD:
          this.handleSubscription({
            _action: "update",
            record: message.data,
          });
          break;
        case BuiltinEvent.REQUEST_ERROR:
          if (
            message.error ==
              "Your message contains content that is not allowed." ||
            message.error == "Anonymous users can only perform read operations"
          ) {
            break;
          } else {
            throw new Error(message.error);
          }
        case FromServer.RoomStateUpdated: {
          // Merge the partial state update with current state
          const updatedState = deepMerge(
            WebsimSocket._roomState,
            message.state
          );

          // Check if there's an actual change
          if (WebsimSocket._roomState !== updatedState) {
            // Update the data
            WebsimSocket._roomState = updatedState;

            // Create a new reference
            WebsimSocket._roomStateRef = { ...WebsimSocket._roomState };

            // Notify subscribers
            WebsimSocket._roomStateSubscriptions.forEach((callback) => {
              if (WebsimSocket._roomStateRef) {
                callback(WebsimSocket._roomStateRef);
              }
            });
          }
          break;
        }
        case BuiltinEvent.GET_ROOM_STATE: {
          // Check if there's an actual change
          if (WebsimSocket._roomState !== message.state) {
            // Update the data
            WebsimSocket._roomState = message.state;

            // Create a new reference
            WebsimSocket._roomStateRef = { ...WebsimSocket._roomState };

            // Notify subscribers
            WebsimSocket._roomStateSubscriptions.forEach((callback) => {
              if (WebsimSocket._roomStateRef) {
                callback(WebsimSocket._roomStateRef);
              }
            });
          }
          break;
        }
        case FromServer.PresenceUpdateRequested: {
          WebsimSocket._presenceUpdateRequestSubscriptions.forEach(
            (callback) => {
              callback(message.updateRequest, message.fromClientId);
            }
          );
          break;
        }
        default: {
          const messageEvent = new MessageEvent("message", {
            data: message,
            bubbles: false,
            cancelable: false,
          });
          this.dispatchEvent(messageEvent);
          break;
        }
      }
    }
  }

  private handleSubscription({
    _action,
    record,
  }: {
    _action: "create" | "update" | "delete";
    record: any;
  }) {
    const type = record.type;

    // Update caches and notify subscribers
    WebsimSocket._collectionCaches.forEach((cache, cacheKey) => {
      if (cacheKey.startsWith(`${type}:`)) {
        const filtersJson = cacheKey.substring(type.length + 1);
        const filters = JSON.parse(filtersJson);

        // Check if the record matches the filters
        const matchesFilters = this.applyFilters([record], filters).length > 0;

        let records = cache;
        let changed = false;

        switch (_action) {
          case "create":
            if (matchesFilters) {
              records = [record, ...records];
              changed = true;
            }
            break;
          case "update":
            if (records.some((item) => item.id === record.id)) {
              if (matchesFilters) {
                records = records.map((item) =>
                  item.id === record.id ? record : item
                );
              } else {
                // Remove the record if it no longer matches filters
                records = records.filter((item) => item.id !== record.id);
              }
              changed = true;
            } else if (matchesFilters) {
              // Record now matches filters, add it
              records = [record, ...records];
              changed = true;
            }
            break;
          case "delete":
            if (records.some((item) => item.id === record.id)) {
              records = records.filter((item) => item.id !== record.id);
              changed = true;
            }
            break;
        }

        if (changed) {
          WebsimSocket._collectionCaches.set(cacheKey, records);

          // Notify subscribers for this cacheKey
          const subs = WebsimSocket._filteredSubscriptions.get(cacheKey);
          if (subs) {
            subs.forEach((callback) => callback(records));
          }
        }
      }
    });
  }

  // Methods for db backed records
  private async createDatabaseRecord(record: {
    [key: string]: any;
  }): Promise<{ [id: string]: any }> {
    const { type, ...data } = record;
    const result = await this._sendRequest(BuiltinEvent.CREATE_DB_RECORD, {
      type,
      data,
    });
    return result as { [id: string]: any };
  }

  private async updateDatabaseRecord(
    id: string,
    data: {
      [key: string]: any;
    }
  ): Promise<{ [id: string]: any }> {
    const result = await this._sendRequest(BuiltinEvent.UPDATE_DB_RECORD, {
      id,
      data,
    });
    return result as { [id: string]: any };
  }

  private async deleteDatabaseRecord(id: string, type: string): Promise<void> {
    await this._sendRequest(BuiltinEvent.DELETE_DB_RECORD, { id, type });
  }

  private async getDatabaseRecords({
    type,
    ...filters
  }: {
    type?: string;
    [key: string]: any;
  } = {}): Promise<any> {
    const data = await this._sendRequest(BuiltinEvent.GET_DB_RECORDS, {
      type,
      filters,
    });
    return data;
  }

  private _sendRequest<T>(
    type: BuiltinEvent,
    payload: any,
    waitForResponse: boolean = true
  ): Promise<T> {
    const requestId =
      WebsimSocket._requestIdPrefix + WebsimSocket._requestIdCounter++;
    return new Promise<T>((resolve, reject) => {
      if (waitForResponse) {
        WebsimSocket._pendingRequests.set(requestId, resolve);
      }

      this.send({
        requestId,
        type,
        payload,
      });
      if (!waitForResponse) {
        resolve(null as any);
      }

      setTimeout(() => {
        if (WebsimSocket._pendingRequests.has(requestId)) {
          WebsimSocket._pendingRequests.delete(requestId);
          reject(new Error(`Request timed out for requestId ${requestId}`));
        }
      }, 30000); // 30s timeout
    });
  }

  collection(type: string): CollectionAPI {
    const collectionImpl = (
      filters: { [key: string]: any } = {}
    ): CollectionAPI => {
      const filtersKey = stableStringify(filters);
      const cacheKey = `${type}:${filtersKey}`;

      if (WebsimSocket._filteredCollections.has(cacheKey)) {
        return WebsimSocket._filteredCollections.get(cacheKey)!;
      }

      const getFilteredList = (): any[] => {
        const cache =
          WebsimSocket._collectionCaches.get(cacheKey) || cachedEmptyList;
        return cache;
      };

      const subscribe = (callback: SubscriptionCallback): (() => void) => {
        let subs = WebsimSocket._filteredSubscriptions.get(cacheKey);
        if (!subs) {
          subs = new Set<SubscriptionCallback>();
          WebsimSocket._filteredSubscriptions.set(cacheKey, subs);
        }
        subs.add(callback);

        // Check if cache exists for this cacheKey
        const cache = WebsimSocket._collectionCaches.get(cacheKey);
        if (cache) {
          // Notify the subscriber with current data
          callback(cache);
        } else {
          // Initiate data fetch if not already fetching
          if (!WebsimSocket._pendingCacheFetches.has(cacheKey)) {
            WebsimSocket._pendingCacheFetches.add(cacheKey);
            this.getDatabaseRecords({ type, ...filters }).then((records) => {
              // Set the cache for this collection
              WebsimSocket._collectionCaches.set(cacheKey, records);
              WebsimSocket._pendingCacheFetches.delete(cacheKey);

              // Notify subscribers
              const subs = WebsimSocket._filteredSubscriptions.get(cacheKey);
              if (subs) {
                subs.forEach((subCallback) => subCallback(records));
              }
            });
          }
          // Subscriber will be notified when data comes back
        }

        // Return unsubscribe function
        return () => {
          subs!.delete(callback);
          if (subs!.size === 0) {
            WebsimSocket._filteredSubscriptions.delete(cacheKey);
          }
        };
      };

      const collectionAPI: CollectionAPI = {
        getList: getFilteredList,
        create: async (data: { [key: string]: any }) => {
          const result = await this.createDatabaseRecord({ type, ...data });
          return result;
        },
        update: async (id: string, data: { [key: string]: any }) => {
          const result = await this.updateDatabaseRecord(id, data);
          return result;
        },
        delete: async (id: string) => {
          await this.deleteDatabaseRecord(id, type);
        },
        subscribe,
        filter: (newFilters: { [key: string]: any }) => {
          return collectionImpl({ ...filters, ...newFilters });
        },
      };

      WebsimSocket._filteredCollections.set(cacheKey, collectionAPI);
      return collectionAPI;
    };

    return collectionImpl();
  }

  // Utility function to apply filters
  private applyFilters(records: any[], filters: { [key: string]: any }): any[] {
    return records.filter((item) => {
      return Object.entries(filters).every(([key, value]) => {
        const itemValue = item[key];
        if (value == null) {
          // Treat undefined and null as equal
          return itemValue == null;
        } else {
          // eslint-disable-next-line eqeqeq
          return itemValue == value;
        }
      });
    });
  }

  /**
   * Updates the current client's presence state.
   * @param presence The new presence state to set.
   */
  private async _updatePresence(presence: Record<string, any>): Promise<void> {
    const clientId = this._client.id;
    const currentPresence = WebsimSocket._presenceData[clientId] || {};

    // Check if there's an actual change
    const mergedPresence = { ...currentPresence, ...presence };
    if (areObjectsEqual(currentPresence, mergedPresence)) {
      return; // No changes, skip update
    }

    // Update the data
    WebsimSocket._presenceData[clientId] = mergedPresence;

    // Create a new reference directly
    WebsimSocket._presenceDataRef = { ...WebsimSocket._presenceData };

    // Notify subscribers with the new reference
    WebsimSocket._presenceSubscriptions.forEach((callback) => {
      if (WebsimSocket._presenceDataRef) {
        callback(WebsimSocket._presenceDataRef);
      }
    });

    // Handle throttling
    if (this._presenceThrottleTimeout === null) {
      // No active throttle - send immediately
      this._sendPresenceToServer(presence);

      // Start a new throttle cycle
      this._startThrottleCycle();
    } else {
      // Throttle active - accumulate updates
      if (!this._pendingPresenceUpdates) {
        this._pendingPresenceUpdates = {};
      }

      // Merge the new presence data with pending updates
      this._pendingPresenceUpdates = {
        ...this._pendingPresenceUpdates,
        ...presence,
      };
    }
  }

  /**
   * Starts a new throttle cycle
   */
  private _startThrottleCycle(): void {
    // Initialize pending updates as empty object
    this._pendingPresenceUpdates = {};

    // Set up throttle timer
    this._presenceThrottleTimeout = window.setTimeout(() => {
      // Capture pending updates
      const updates = this._pendingPresenceUpdates;

      // Reset throttle state
      this._presenceThrottleTimeout = null;
      this._pendingPresenceUpdates = null;

      // If there are pending updates, send them and start a new cycle
      if (updates && Object.keys(updates).length > 0) {
        this._sendPresenceToServer(updates);
        this._startThrottleCycle(); // Continue throttling
      }
      // If no pending updates, we're done throttling until next update
    }, this._presenceThrottleMs);
  }

  /**
   * Sends presence data to the server.
   * @param presence The presence data to send.
   */
  private async _sendPresenceToServer(
    presence: Record<string, any>
  ): Promise<void> {
    await this._sendRequest(BuiltinEvent.UPDATE_PRESENCE, { presence }, false);
  }

  /**
   * Gets the current presence state of all connected clients.
   */
  private async getPresence(): Promise<void> {
    await this._sendRequest(BuiltinEvent.GET_PRESENCE, {});
  }

  /**
   * Updates the room-wide state.
   * @param delta The new state to merge with current room state.
   */
  async updateRoomState(delta: Record<string, any>): Promise<void> {
    const currentState = WebsimSocket._roomState;
    const mergedState = deepMerge(currentState, delta);

    // Update the local data immediately
    WebsimSocket._roomState = mergedState;
    WebsimSocket._roomStateRef = { ...WebsimSocket._roomState };

    // Notify subscribers
    WebsimSocket._roomStateSubscriptions.forEach((callback) => {
      if (WebsimSocket._roomStateRef) {
        callback(WebsimSocket._roomStateRef);
      }
    });

    // Handle throttling with changed keys only
    if (this._roomStateThrottleTimeout === null) {
      // No active throttle - send immediately
      this._sendRoomStateToServer(delta);
      this._startRoomStateThrottleCycle();
    } else {
      // Throttle active - accumulate updates
      if (!this._pendingRoomStateUpdates) {
        this._pendingRoomStateUpdates = {};
      }
      // Merge only the changed keys
      this._pendingRoomStateUpdates = deepMerge(
        this._pendingRoomStateUpdates,
        delta,
        { preserveNull: true }
      );
    }
  }

  private _startRoomStateThrottleCycle(): void {
    this._pendingRoomStateUpdates = {};

    this._roomStateThrottleTimeout = window.setTimeout(() => {
      const updates = this._pendingRoomStateUpdates;

      this._roomStateThrottleTimeout = null;
      this._pendingRoomStateUpdates = null;

      if (updates && Object.keys(updates).length > 0) {
        this._sendRoomStateToServer(updates);
        this._startRoomStateThrottleCycle();
      }
    }, this._roomStateThrottleMs);
  }

  private async _sendRoomStateToServer(
    state: Record<string, any>
  ): Promise<void> {
    await this._sendRequest(BuiltinEvent.UPDATE_ROOM_STATE, { state }, false);
  }

  /**
   * Gets the current room state.
   */
  private async getRoomState(): Promise<void> {
    await this._sendRequest(BuiltinEvent.GET_ROOM_STATE, {});
  }

  // Add new getters that mirror the party API
  get clientId(): string {
    return this._client.id;
  }

  get presence(): { [clientId: string]: Record<string, any> } {
    return this.party.presence;
  }

  get roomState(): Record<string, any> {
    return this.party.roomState;
  }

  // TODO: upstream this fix to _connectedPeers (client should be included in the list of peers, as we say in the docs)
  get peers(): { [clientId: string]: { avatarUrl: string; username: string } } {
    const peers = {
      ...WebsimSocket._connectedPeers,
      [this.clientId]: {
        avatarUrl: this._client.avatarUrl,
        username: this._client.username,
      },
    };
    return peers;
  }

  // Add method aliases
  async initialize(): Promise<void> {
    return (
      this._initializePromise || Promise.reject("WebsimSocket not initialized")
    );
  }

  updatePresence(presence: Record<string, any>): void {
    this.party.updatePresence(presence);
  }

  subscribePresence(
    callback: (presence: { [clientId: string]: Record<string, any> }) => void
  ): () => void {
    return this.party.subscribePresence(callback);
  }

  subscribeRoomState(
    callback: (state: Record<string, any>) => void
  ): () => void {
    WebsimSocket._roomStateSubscriptions.add(callback);
    if (!WebsimSocket._roomStateRef) {
      WebsimSocket._roomStateRef = { ...WebsimSocket._roomState };
    }
    callback(WebsimSocket._roomStateRef);
    return () => {
      WebsimSocket._roomStateSubscriptions.delete(callback);
    };
  }

  requestPresenceUpdate(clientId: string, update: Record<string, any>): void {
    const clonedUpdate = structuredClone(update);
    // TODO: add types to these non-generic requests
    this._sendRequest(
      BuiltinEvent.PRESENCE_REQUEST_UPDATE,
      {
        targetClientId: clientId,
        update: clonedUpdate,
      },
      false
    );
  }

  subscribePresenceUpdateRequests(
    callback: (updateRequest: Record<string, any>, fromClientId: string) => void
  ): () => void {
    WebsimSocket._presenceUpdateRequestSubscriptions.add(callback);
    return () => {
      WebsimSocket._presenceUpdateRequestSubscriptions.delete(callback);
    };
  }
}
